import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { BrowserProvider } from 'ethers';

function FaucetModal({
  isOpen,
  onClose,
  faucetConfig,
  walletAddress,
  selectedWalletType,
  walletManager,
  ownedNFTs,
  hasAccess,
  onError
}) {
  const [status, setStatus] = useState('ready'); // ready, claiming, success, error
  const [result, setResult] = useState(null);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStatus('ready');
      setResult(null);
      setAnimating(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClaim = async () => {
    if (!hasAccess) {
      onError({
        title: 'Access Denied',
        message: 'You do not have the required NFTs to access the faucet.',
        type: 'warning'
      });
      return;
    }

    setStatus('claiming');
    setAnimating(true);

    try {
      // Get the correct provider based on wallet type
      let ethereumProvider;
      if (selectedWalletType === 'walletconnect') {
        // For WalletConnect, use the provider from walletManager
        console.log('🔗 Using WalletConnect provider from walletManager for faucet');
        ethereumProvider = walletManager.walletConnectProvider;
        if (!ethereumProvider) {
          throw new Error('WalletConnect provider not found. Please reconnect your wallet.');
        }
      } else if (selectedWalletType === 'coinbase') {
        // For Coinbase Wallet, use the provider from walletManager
        console.log('💼 Using Coinbase Wallet provider from walletManager for faucet');
        ethereumProvider = walletManager.coinbaseProvider;
        if (!ethereumProvider) {
          throw new Error('Coinbase Wallet provider not found. Please reconnect your wallet.');
        }
      } else if (selectedWalletType === 'magiceden') {
        ethereumProvider = window.magicEden?.ethereum;
      } else {
        ethereumProvider = window.ethereum;
      }

      // For all wallets including Magic Eden, switch to Polygon network
      // (Magic Eden EVM provider supports wallet_switchEthereumChain per their sandbox)
      await ethereumProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x89' }], // Polygon mainnet
      });

      // For Magic Eden, get address from multichain but use ethereum provider for transactions
      let provider, signer, userAddress;
      if (selectedWalletType === 'magiceden') {
        const multiChain = window.magicEden?.multiChain;
        if (multiChain && multiChain.isConnected) {
          // Magic Eden multiChain detected - getting EVM address
          // Get ethereum address from multichain (works for Polygon)
          const multichainResponse = multiChain.currentMultiChainResponse;
          if (multichainResponse?.addresses?.ethereum) {
            // Magic Eden returns addresses as an array, take the first one
            const ethAddresses = multichainResponse.addresses.ethereum;
            userAddress = Array.isArray(ethAddresses) ? ethAddresses[0] : ethAddresses;
            // Use ethereum provider for actual transactions (Magic Eden pattern)
            provider = new BrowserProvider(ethereumProvider);
            signer = await provider.getSigner();
          } else {
            // Magic Eden multiChain missing EVM address, falling back to ethereum provider
            provider = new BrowserProvider(ethereumProvider);
            signer = await provider.getSigner();
            userAddress = await signer.getAddress();
          }
        } else {
          // Magic Eden multiChain not available, using ethereum provider
          provider = new BrowserProvider(ethereumProvider);
          signer = await provider.getSigner();
          userAddress = await signer.getAddress();
        }
      } else {
        // For MetaMask, Coinbase, WalletConnect, and others - use standard ethereum provider
        provider = new BrowserProvider(ethereumProvider);
        signer = await provider.getSigner();
        userAddress = await signer.getAddress();
      }

      // Create signature for authentication
      const timestamp = Math.floor(Date.now() / 60000);
      const message = `POL Faucet Claim\nWallet: ${userAddress}\nTimestamp: ${timestamp}`;
      const signature = await signer.signMessage(message);

      // Call faucet API
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/faucet?action=claim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          wallet: userAddress,
          signature: signature,
          timestamp: timestamp,
          ownedNFTs: ownedNFTs
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to claim from faucet');
      }

      setResult(data);
      setStatus('success');

    } catch (error) {
      console.error('Faucet claim error:', error);
      setStatus('error');
      setResult({ error: error.message });

      if (onError) {
        // Extract the actual error message from nested error objects
        let actualErrorMessage = error.message || '';
        
        // Check for nested error messages (common with Ledger and other hardware wallets)
        if (error.data?.cause?.message) {
          actualErrorMessage = error.data.cause.message;
        } else if (error.cause?.message) {
          actualErrorMessage = error.cause.message;
        } else if (error.error?.message) {
          actualErrorMessage = error.error.message;
        }
        
        // If we still have the full error object as a string, try to extract just the message
        if (actualErrorMessage.includes('could not coalesce error') && actualErrorMessage.includes('"message":')) {
          try {
            const match = actualErrorMessage.match(/"message":\s*"([^"]+)"/);
            if (match && match[1]) {
              actualErrorMessage = match[1];
            }
          } catch (e) {
            // If parsing fails, keep the original message
          }
        }

        // Check for user cancellation
        if (error.code === 4001 || error.code === -32603 || error.code === 'ACTION_REJECTED' || 
            actualErrorMessage?.includes('user denied') || 
            actualErrorMessage?.includes('User rejected the request') || 
            actualErrorMessage?.includes('ethers-user-denied') ||
            actualErrorMessage?.includes('User rejected')) {
          onError({
            title: 'Transaction Cancelled',
            message: 'You cancelled the faucet claim. No POL tokens were sent.',
            type: 'info'
          });
          // Close the faucet modal when showing error
          onClose();

        // Check for Ledger-specific errors
        } else if (actualErrorMessage?.includes('Ledger: Ethereum app closed') || 
                   actualErrorMessage?.includes('Ledger:') ||
                   actualErrorMessage?.includes('Ethereum app closed')) {
          onError({
            title: 'Ledger Wallet Issue',
            message: 'Your Ledger device needs attention. Please open the Ethereum app on your Ledger device and try again.',
            type: 'warning'
          });
          // Close the faucet modal when showing error
          onClose();

        // Check for network switching issues
        } else if (actualErrorMessage?.includes('wallet_switchEthereumChain is not supported')) {
          onError({
            title: 'Manual Network Switch Required',
            message: 'Your wallet doesn\'t support automatic network switching. Please manually switch to Polygon network in your wallet settings before claiming from the faucet.',
            type: 'warning'
          });
          // Close the faucet modal when showing error
          onClose();

        // Check for connection issues
        } else if (actualErrorMessage?.includes('not connected') || 
                   actualErrorMessage?.includes('connection') ||
                   actualErrorMessage?.includes('disconnected')) {
          onError({
            title: 'Wallet Connection Issue',
            message: 'There seems to be a connection issue with your wallet. Please try reconnecting your wallet and try again.',
            type: 'warning'
          });
          // Close the faucet modal when showing error
          onClose();

        // Generic error fallback
        } else {
          onError({
            title: 'Faucet Claim Failed',
            message: actualErrorMessage || 'An error occurred while claiming from the faucet.',
            type: 'error'
          });
          // Close the faucet modal when showing error
          onClose();
        }
      }
    } finally {
      setAnimating(false);
    }
  };

  const handleDone = () => {
    setStatus('ready');
    setResult(null);
    onClose();
  };

  const modalContent = (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '500px', textAlign: 'center' }}>
        
        {status === 'ready' && (
          <>
            <h2 style={{ color: '#00bfff', marginBottom: '1rem' }}>
              🚰 POL Faucet
            </h2>
            
            <div className="faucet-container" style={{
              position: 'relative',
              margin: '2rem auto',
              width: '200px',
              height: '200px'
            }}>
              {/* Animated Faucet */}
              <div className="faucet-tap" style={{
                position: 'absolute',
                top: '0',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100px',
                height: '60px',
                background: 'linear-gradient(135deg, #4a90e2, #00bfff, #87ceeb)',
                borderRadius: '10px 10px 50px 50px',
                border: '3px solid #00bfff',
                boxShadow: '0 0 20px rgba(0, 191, 255, 0.5)',
                animation: hasAccess ? 'faucetPulse 2s ease-in-out infinite' : 'none'
              }}>
                <div style={{
                  position: 'absolute',
                  bottom: '-10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '20px',
                  height: '20px',
                  background: '#00bfff',
                  borderRadius: '50%',
                  border: '2px solid #ffffff'
                }} />
              </div>

              {/* POL Drops */}
              {hasAccess && (
                <>
                  <div className="pol-drop" style={{
                    position: 'absolute',
                    top: '80px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '8px',
                    height: '12px',
                    background: 'linear-gradient(180deg, #8a2be2, #00bfff)',
                    borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
                    animation: 'polDrop 1.5s ease-in-out infinite'
                  }} />
                  <div className="pol-drop" style={{
                    position: 'absolute',
                    top: '80px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '8px',
                    height: '12px',
                    background: 'linear-gradient(180deg, #8a2be2, #00bfff)',
                    borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
                    animation: 'polDrop 1.5s ease-in-out infinite 0.5s'
                  }} />
                </>
              )}

              {/* Collection Basin */}
              <div style={{
                position: 'absolute',
                bottom: '0',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '120px',
                height: '40px',
                background: 'linear-gradient(135deg, #1a1a1a, #2d2d2d)',
                border: '2px solid #00bfff',
                borderRadius: '10px',
                boxShadow: 'inset 0 0 15px rgba(0, 191, 255, 0.3)'
              }}>
                {hasAccess && (
                  <div style={{
                    position: 'absolute',
                    bottom: '5px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '80px',
                    height: '8px',
                    background: 'linear-gradient(90deg, #8a2be2, #00bfff, #8a2be2)',
                    borderRadius: '10px',
                    animation: 'polGlow 2s ease-in-out infinite'
                  }} />
                )}
              </div>
            </div>

            <div style={{ margin: '1.5rem 0' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#00bfff', marginBottom: '0.5rem' }}>
                {faucetConfig?.amount_pol || '0.01'} POL Tokens
              </div>
              <div style={{ fontSize: '0.9rem', color: '#888' }}>
                Perfect for 2-3 transactions on Polygon
              </div>
            </div>

            {hasAccess ? (
              <>
                <div style={{
                  padding: '1rem',
                  background: 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '0.5rem',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{ color: '#22c55e', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    ✅ VIP Access Granted
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#888' }}>
                    You own the required NFTs to access this exclusive faucet
                  </div>
                </div>

                <button
                  className="rainbowBtn"
                  onClick={handleClaim}
                  style={{ width: '100%', fontSize: '1.1rem', padding: '1rem' }}
                >
                  💧 Claim POL Tokens
                </button>
              </>
            ) : (
              <div style={{
                padding: '1rem',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '0.5rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{ color: '#ef4444', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  🔒 VIP Access Required
                </div>
                <div style={{ fontSize: '0.9rem', color: '#888', marginBottom: '1rem' }}>
                  This faucet is exclusive to our VIP NFT holders. You need to own one of our special collections to access it.
                </div>
                
                {faucetConfig?.token_gate_requirements && faucetConfig.token_gate_requirements.length > 0 && (
                  <div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                      Required Collections:
                    </div>
                    {faucetConfig.token_gate_requirements.map((req, i) => (
                      <div key={i} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.5rem',
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '0.25rem',
                        marginBottom: '0.5rem'
                      }}>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{req.name}</div>
                          <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                            {req.quantity} NFT{req.quantity > 1 ? 's' : ''} required
                          </div>
                        </div>
                        {req.marketplace_link && (
                          <a
                            href={req.marketplace_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: '#8a2be2',
                              color: 'white',
                              textDecoration: 'none',
                              borderRadius: '0.25rem',
                              fontSize: '0.8rem'
                            }}
                          >
                            View on Marketplace
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div style={{
              padding: '1rem',
              background: 'rgba(138, 43, 226, 0.1)',
              border: '1px solid rgba(138, 43, 226, 0.3)',
              borderRadius: '0.5rem',
              marginTop: '1rem'
            }}>
              <div style={{ fontSize: '0.9rem', color: '#888', textAlign: 'left' }}>
                <strong style={{ color: '#8a2be2' }}>What is a faucet?</strong><br />
                A crypto faucet gives you a small amount of tokens for free. Our VIP faucet helps our NFT holders get POL tokens when they need a little boost to use our platform or make transactions on Polygon.
              </div>
            </div>

            <button
              onClick={onClose}
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                background: 'transparent',
                border: '1px solid #888',
                color: '#888',
                borderRadius: '0.5rem',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </>
        )}

        {status === 'claiming' && (
          <>
            <h2 style={{ color: '#00bfff' }}>💧 Claiming POL Tokens...</h2>
            <div className="faucet-container" style={{
              position: 'relative',
              margin: '2rem auto',
              width: '200px',
              height: '200px'
            }}>
              <div className="faucet-tap" style={{
                position: 'absolute',
                top: '0',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100px',
                height: '60px',
                background: 'linear-gradient(135deg, #4a90e2, #00bfff, #87ceeb)',
                borderRadius: '10px 10px 50px 50px',
                border: '3px solid #00bfff',
                boxShadow: '0 0 30px rgba(0, 191, 255, 0.8)',
                animation: 'faucetActive 0.5s ease-in-out infinite'
              }}>
                <div style={{
                  position: 'absolute',
                  bottom: '-10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '20px',
                  height: '20px',
                  background: '#00bfff',
                  borderRadius: '50%',
                  border: '2px solid #ffffff',
                  animation: 'polPulse 0.3s ease-in-out infinite'
                }} />
              </div>

              {/* Multiple animated drops while claiming */}
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="pol-drop"
                  style={{
                    position: 'absolute',
                    top: '80px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '10px',
                    height: '15px',
                    background: 'linear-gradient(180deg, #8a2be2, #00bfff)',
                    borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
                    animation: `polDropFast 0.8s ease-in-out infinite ${i * 0.2}s`
                  }}
                />
              ))}
            </div>
            <div style={{ color: '#888' }}>
              Processing your claim... Please wait for the transaction to confirm.
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <h2 style={{ color: '#22c55e' }}>✅ POL Tokens Claimed!</h2>
            <div style={{ margin: '1.5rem 0' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#22c55e', marginBottom: '0.5rem' }}>
                {result?.amount} POL sent to your wallet
              </div>
              {result?.txHash && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong>Transaction:</strong>{' '}
                  <a
                    href={`https://polygonscan.com/tx/${result.txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#8a2be2', textDecoration: 'underline' }}
                  >
                    View on PolygonScan
                  </a>
                </div>
              )}
              {result?.nextClaimTime && (
                <div style={{ fontSize: '0.9rem', color: '#888' }}>
                  Next claim available: {new Date(result.nextClaimTime).toLocaleDateString()}
                </div>
              )}
            </div>
            
            <div style={{
              padding: '1rem',
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              borderRadius: '0.5rem',
              marginBottom: '1rem'
            }}>
              <div style={{ fontSize: '0.9rem', color: '#888' }}>
                🎉 Enjoy your POL tokens! You now have enough for several transactions on our platform.
              </div>
            </div>

            <button 
              className="rainbowBtn" 
              onClick={handleDone}
              style={{ width: '100%' }}
            >
              Awesome!
            </button>
          </>
        )}

        <style>
          {`
            @keyframes faucetPulse {
              0%, 100% { box-shadow: 0 0 20px rgba(0, 191, 255, 0.5); }
              50% { box-shadow: 0 0 30px rgba(0, 191, 255, 0.8); }
            }
            
            @keyframes faucetActive {
              0%, 100% { transform: translateX(-50%) scale(1); }
              50% { transform: translateX(-50%) scale(1.05); }
            }
            
            @keyframes polDrop {
              0% { 
                opacity: 0;
                transform: translateX(-50%) translateY(0px);
              }
              20% {
                opacity: 1;
              }
              100% { 
                opacity: 0;
                transform: translateX(-50%) translateY(100px);
              }
            }
            
            @keyframes polDropFast {
              0% { 
                opacity: 0;
                transform: translateX(-50%) translateY(0px);
              }
              20% {
                opacity: 1;
              }
              100% { 
                opacity: 0;
                transform: translateX(-50%) translateY(120px);
              }
            }
            
            @keyframes polGlow {
              0%, 100% { opacity: 0.6; }
              50% { opacity: 1; }
            }
            
            @keyframes polPulse {
              0%, 100% { transform: translateX(-50%) scale(1); }
              50% { transform: translateX(-50%) scale(1.2); }
            }
          `}
        </style>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default FaucetModal; 